/**
 * Renders the rich-text subset from `richText.ts` as React elements.
 *
 * Text nodes go through React, so nothing typed into a paragraph can become
 * markup; the only elements ever emitted are <strong>, <em>, <br> and an <a>
 * that opens in a new tab with `rel="noopener noreferrer"`.
 */
import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { parseInlineText, parseRichText } from './richText';
import type { RichNode } from './richText';

function renderNodes(nodes: RichNode[]): ReactNode[] {
  return nodes.map((n, i) => {
    if (typeof n === 'string') return <Fragment key={i}>{n}</Fragment>;
    if (n.t === 'br') return <br key={i} />;
    if (n.t === 'b') return <strong key={i}>{renderNodes(n.c)}</strong>;
    if (n.t === 'i') return <em key={i}>{renderNodes(n.c)}</em>;
    return <a key={i} href={n.href} target="_blank" rel="noopener noreferrer">{renderNodes(n.c)}</a>;
  });
}

/** Inline: the whole text as one run, paragraphs joined by a line break. Use inside a <p>. */
export function RichInline({ text }: { text: string }) {
  return <>{renderNodes(parseInlineText(text))}</>;
}

/** Block: one <p> per paragraph, inside a wrapper carrying `className`. */
export function RichBlocks({ text, className }: { text: string; className?: string }) {
  const blocks = parseRichText(text);
  if (!blocks.length) return null;
  return (
    <div className={className}>
      {blocks.map((b, i) => <p key={i}>{renderNodes(b)}</p>)}
    </div>
  );
}
